import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { generatePath, useNavigate, useParams } from 'react-router-dom';

import {
  SpaceDiscriminator,
  useGetBuildingsByIdQuery,
  useGetCommercialSpacesQuery,
  useGetSpacesByIdQuery,
  useGetStatisticsVacancyQuery,
} from '@client/api';
import {
  Card,
  CardContent,
  CardHeader,
  CardProps,
  CardTitle,
  isDefinedString,
  isReadModelExisting,
  Pagination,
  Table,
  TableContent,
  TableHead,
  TableHeadCell,
  TableHeadRow,
  Text,
  Typography,
  useAreaMetricString,
} from '@client/toolkit';

import { CommercialSpacesListItem } from './commercial-spaces-list-item';

import { PortfolioPaths } from '../../portfolio.router';
import { SpaceCardActions } from '../../buildings/building/dashboard/components/space-card-actions/space-card-actions';

const COMMERCIAL_SPACE_DISCRIMINATOR: SpaceDiscriminator = 'CommercialSpace';

export type CommercialSpacesListCardProps = {
  className?: string;
  variant?: CardProps['variant'];
};

export function CommercialSpacesListCard({ className, variant }: CommercialSpacesListCardProps) {
  const { t: tGlobal } = useTranslation('global');
  const { t: tCommon } = useTranslation();
  const { t: tType } = useTranslation('type');

  const navigate = useNavigate();

  const { buildingId, spaceId } = useParams<{ buildingId: string; spaceId: string }>();

  const {
    data: space,
    isFetching: isFetchingSpace,
    isLoading: isLoadingSpace,
  } = useGetSpacesByIdQuery(
    {
      id: spaceId ?? '',
    },
    { skip: !spaceId },
  );

  const {
    data: building,
    isFetching: isFetchingBuilding,
    isLoading: isLoadingBuilding,
  } = useGetBuildingsByIdQuery(
    {
      id: buildingId ?? '',
    },
    { skip: !buildingId || !!spaceId },
  );

  const isFetchingParent = isFetchingBuilding || isFetchingSpace;
  const isLoadingParent = isLoadingSpace || isLoadingBuilding;

  const ancestorPath = useMemo(() => space?.path || building?.path, [building?.path, space?.path]);

  const pageSize = 20;
  const [page, setPage] = useState(0);
  const pageOffset = page * pageSize;

  const {
    data,
    isFetching,
    isLoading: isLoadingList,
  } = useGetCommercialSpacesQuery(
    {
      'sort.Property': 'Name',
      'sort.Order': 'Asc',
      'ancestorPath.Equal': ancestorPath,
      'pagination.Limit': pageSize,
      'pagination.Offset': pageOffset,
    },
    { skip: !ancestorPath },
  );

  const isLoading = isLoadingList || isLoadingParent;

  const items = useMemo(() => data?.items ?? [], [data?.items]);
  const hasItems = useMemo(() => items.length > 0, [items]);
  const totalCount = data?.totalCount ?? 0;

  const { data: vacancyReport, isFetching: isFetchingStatistics } = useGetStatisticsVacancyQuery(
    {
      'ancestorPath.Equal': ancestorPath,
      'discriminator.Contains': ['CommercialSpace'],
    },
    { skip: !ancestorPath },
  );

  const totalArea = useAreaMetricString(vacancyReport?.total?.area);

  const handleClickCreateSpace = () => {
    if (!buildingId && !isReadModelExisting(space)) return;

    if (isReadModelExisting(space)) {
      navigate(
        generatePath(PortfolioPaths.SpaceChildCreate, {
          buildingId,
          spaceId: space.id,
          discriminator: space.discriminator,
          createDiscriminator: 'CommercialSpace',
        }),
      );
    } else {
      navigate(
        generatePath(PortfolioPaths.BuildingChildCreate, { buildingId, createDiscriminator: 'CommercialSpace' }),
      );
    }
  };

  return (
    <Card className={className} variant={variant}>
      <CardHeader>
        <div className="flex-1">
          <CardTitle variant="dense">
            {tType(`SpaceDiscriminator.${COMMERCIAL_SPACE_DISCRIMINATOR}`, { context: 'plural' })}
          </CardTitle>
          <Typography className="mt-2 -mb-1" variant="sub">
            {tCommon('totalEntity', { entity: tGlobal('commonArea.areaLower') })}

            <Text
              className="ml-1.5 font-bold text-white"
              isEmpty={!isDefinedString(totalArea)}
              loading={isFetchingStatistics || isFetchingParent}
              SkeletonProps={{ className: 'w-[46px]', inline: true }}
            >
              {totalArea}
            </Text>
          </Typography>
        </div>
        <SpaceCardActions
          loading={isFetchingBuilding}
          spaceId={buildingId ?? ''}
          handleCreate={handleClickCreateSpace}
        />
      </CardHeader>

      <CardContent variant="fill">
        <Table className="overflow-hidden rounded-md">
          <TableHead>
            <TableHeadRow>
              <TableHeadCell>{tGlobal('space.areaName')}</TableHeadCell>
              <TableHeadCell className="w-[180px]">{tGlobal('entity.tenant')}</TableHeadCell>
              <TableHeadCell className="w-[140px]">{tGlobal('agreement.agreementStatus')}</TableHeadCell>
              <TableHeadCell className="w-[140px]">{tGlobal('space.status')}</TableHeadCell>
              <TableHeadCell className="w-[150px]">{tGlobal('space.area')}</TableHeadCell>
              {/* COMMENTED OUT: Standard leie - uncomment to restore
              <TableHeadCell align="right" className="w-[250px]">
                {tGlobal('space.rent')}
              </TableHeadCell>
              */}
            </TableHeadRow>
          </TableHead>

          <TableContent
            list={items?.map((x) => <CommercialSpacesListItem key={x.id} space={x} isFetching={isFetching} />)}
            skeleton={Array.from(Array(3).keys()).map((x) => (
              <CommercialSpacesListItem key={x} isFetching />
            ))}
            emptyCaption={tCommon('noEntityFound', { entity: tGlobal('entity.space', { context: 'plural' }) })}
            initializing={isLoading}
            fetching={isFetching}
            empty={!hasItems}
          />
        </Table>
        <Pagination
          className="border-t"
          totalCount={totalCount}
          currentPage={page}
          setPage={setPage}
          pageSize={pageSize}
        />
      </CardContent>
    </Card>
  );
}
